import { useEffect, useRef, useState } from "react";
import { FileText, ImagePlus, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { resolveImg } from "../images";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "apartment", label: "Appartement" },
  { value: "house", label: "Maison" },
  { value: "villa", label: "Villa" },
  { value: "plot", label: "Terrain" },
  { value: "building", label: "Immeuble" },
  { value: "commercial", label: "Commercial" },
  { value: "approved_projects", label: "Projet autorisé" },
];
const CAT_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const EMPTY = {
  title: "",
  title_de: "",
  title_en: "",
  category: "apartment",
  listing_type: "sale",
  price: "",
  monthly_net_rent: "",
  monthly_charges: "",
  monthly_charges_note: "",
  monthly_gross_rent: "",
  commercial_pricing_mode: "per_sqm_year",
  rental_price_per_sqm_year: "",
  annual_rent: "",
  rental_deposit: "",
  availability_date: "",
  availability_status: "available",
  rooms: "",
  area: "",
  usable_area: "",
  zip_code: "",
  city: "",
  description: "",
  description_de: "",
  description_en: "",
  images: [],
  published: true,
  floor: "",
  year_built: "",
  renovation_year: "",
  land_area: "",
  availability: "",
  features: "",
  features_de: "",
  features_en: "",
  extra_facts: "",
  extra_facts_de: "",
  extra_facts_en: "",
  off_market: false,
  sold: false,
};

const inputCls =
  "w-full border-0 border-b border-ivory/25 bg-transparent px-0 py-2 text-sm text-ivory outline-none transition-colors duration-300 placeholder:text-ivory/30 focus:border-champagne";
const labelCls = "mb-1 block text-[10px] uppercase tracking-[0.2em] text-ivory/45";

const adminPropertyPrice = (p) => {
  if (p.listing_type !== "rental") return `CHF ${Number(p.price).toLocaleString("de-CH")}`;
  const commercialArea = Number(p.usable_area || p.area || 0);
  const annual = Number(p.annual_rent || (Number(p.rental_price_per_sqm_year) || 0) * commercialArea);
  const mode = p.commercial_pricing_mode || (Number(p.rental_price_per_sqm_year) > 0 ? "per_sqm_year" : "monthly_fixed");
  if (p.category === "commercial" && mode === "per_sqm_year" && annual > 0) {
    const rate = Number(p.rental_price_per_sqm_year || (commercialArea ? annual / commercialArea : 0));
    return `CHF ${rate.toLocaleString("de-CH")}.– / m² / an · CHF ${annual.toLocaleString("de-CH")}.– / an`;
  }
  const monthly = Number(p.monthly_gross_rent || ((p.monthly_net_rent || 0) + (p.monthly_charges || 0)));
  return `CHF ${monthly.toLocaleString("de-CH")}.– / mois`;
};

const parseChargesInput = (value) => {
  const raw = String(value ?? "").trim();
  const normalized = raw.replace(/[\s']/g, "");
  const match = normalized.match(/\d+(?:\.\d+)?/);
  const numeric = match ? parseFloat(match[0]) : 0;
  return {
    numeric,
    note: raw && (!match || match[0] !== normalized) ? raw : "",
  };
};



export default function PropertyManager({ token, setError }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notifyInfo, setNotifyInfo] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [localPreviews, setLocalPreviews] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [view, setView] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dossierFor, setDossierFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const fileRef = useRef(null);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () =>
    fetch(`${API}/api/admin/properties`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setItems(d.properties))
      .catch(() => setError("Chargement impossible."));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImages = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const previews = list.map((f) => URL.createObjectURL(f));
    setLocalPreviews((ps) => [...ps, ...previews]);
    setUploading((n) => n + list.length);
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API}/api/admin/upload-image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      } catch {
        setError(`Upload impossible: ${file.name}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    setLocalPreviews((ps) => ps.filter((p) => !previews.includes(p)));
    previews.forEach((p) => URL.revokeObjectURL(p));
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadDossier = async (p, lang) => {
    try {
      const res = await fetch(`${API}/api/admin/properties/${p.id}/dossier?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FLEN_Dossier_${p.ref || p.id}_${lang.toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Génération du dossier impossible.");
    }
  };

  const importDossier = async (file) => {
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/import-dossier`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "failed");
      }
      await res.json();
      await load();
      setView("drafts");
    } catch (err) {
      setError(`Import impossible: ${err.message}`);
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const moveImage = (from, to) => {
    if (from === null || from === to) return;
    setForm((f) => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...f, images: imgs };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const residentialArea = parseFloat(form.area) || 0;
    const usableArea = parseFloat(form.usable_area) || 0;
    const isCommercialRental = form.listing_type === "rental" && form.category === "commercial";
    const areaValue = isCommercialRental ? 0 : residentialArea;
    const commercialArea = usableArea || residentialArea;
    const enteredRate = parseFloat(form.rental_price_per_sqm_year) || 0;
    const existingAnnualRent = parseFloat(form.annual_rent) || 0;
    const commercialPricingMode = isCommercialRental ? form.commercial_pricing_mode || "per_sqm_year" : "per_sqm_year";
    const fixedMonthlyNet = parseFloat(form.monthly_net_rent) || 0;
    const annualRent = isCommercialRental
      ? commercialPricingMode === "per_sqm_year"
        ? enteredRate > 0 && commercialArea > 0
          ? Math.round(enteredRate * commercialArea * 100) / 100
          : existingAnnualRent
        : Math.round(fixedMonthlyNet * 12 * 100) / 100
      : 0;
    const rentalRate = isCommercialRental && commercialPricingMode === "per_sqm_year"
      ? enteredRate > 0
        ? enteredRate
        : commercialArea > 0 && annualRent > 0
          ? Math.round((annualRent / commercialArea) * 100) / 100
          : 0
      : 0;
    const netRent = form.listing_type === "rental"
      ? isCommercialRental
        ? commercialPricingMode === "per_sqm_year"
          ? Math.round((annualRent / 12) * 100) / 100
          : fixedMonthlyNet
        : parseFloat(form.monthly_net_rent) || 0
      : 0;
    const parsedCharges = parseChargesInput(form.monthly_charges);
    const charges = parsedCharges.numeric;
    const chargesNote = form.listing_type === "rental" ? parsedCharges.note : "";
    const grossRent = Math.round((netRent + charges) * 100) / 100;
    const body = {
      title: form.title,
      category: form.category,
      listing_type: form.listing_type,
      price: form.listing_type === "rental" ? 0 : parseFloat(form.price) || 0,
      monthly_net_rent: form.listing_type === "rental" ? netRent : 0,
      monthly_charges: form.listing_type === "rental" ? charges : 0,
      monthly_charges_note: form.listing_type === "rental" ? chargesNote : "",
      monthly_gross_rent: form.listing_type === "rental" ? grossRent : 0,
      commercial_pricing_mode: commercialPricingMode,
      rental_price_per_sqm_year: rentalRate,
      annual_rent: annualRent,
      rental_deposit: form.listing_type === "rental" ? parseFloat(form.rental_deposit) || 0 : 0,
      availability_date: form.listing_type === "rental" ? form.availability_date : "",
      availability_status: form.listing_type === "rental" ? form.availability_status : "available",
      rooms: parseFloat(form.rooms) || 0,
      area: areaValue,
      usable_area: isCommercialRental ? commercialArea : usableArea,
      zip_code: form.zip_code,
      city: form.city,
      description: form.description,
      description_de: form.description_de,
      description_en: form.description_en,
      images: form.images,
      published: form.published,
      floor: form.floor,
      year_built: form.year_built,
      renovation_year: form.renovation_year,
      land_area: parseFloat(form.land_area) || 0,
      availability: form.listing_type === "rental" ? "" : form.availability,
      features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      features_de: form.features_de.split("\n").map((s) => s.trim()).filter(Boolean),
      features_en: form.features_en.split("\n").map((s) => s.trim()).filter(Boolean),
      extra_facts: form.extra_facts.split("\n").map((s) => s.trim()).filter(Boolean),
      extra_facts_de: form.extra_facts_de.split("\n").map((s) => s.trim()).filter(Boolean),
      extra_facts_en: form.extra_facts_en.split("\n").map((s) => s.trim()).filter(Boolean),
      off_market: form.off_market,
      sold: form.sold || false,
    };
    try {
      const res = await fetch(editId ? `${API}/api/admin/properties/${editId}` : `${API}/api/admin/properties`, {
        method: editId ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("failed");
      await load();
      setForm(EMPTY);
      setEditId(null);
      setFormOpen(false);
    } catch {
      setError("Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setForm({
      ...EMPTY,
      ...p,
      images: p.images || [],
      features: (p.features || []).join("\n"),
      features_de: (p.features_de || []).join("\n"),
      features_en: (p.features_en || []).join("\n"),
      extra_facts: (p.extra_facts || []).join("\n"),
      extra_facts_de: (p.extra_facts_de || []).join("\n"),
      extra_facts_en: (p.extra_facts_en || []).join("\n"),
      monthly_charges: p.monthly_charges_note || p.monthly_charges,
    });
    setEditId(p.id);
    setFormOpen(true);
  };

  const togglePublish = async (p) => {
    try {
      const res = await fetch(`${API}/api/admin/properties/${p.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ ...p, published: !p.published }),
      });
      if (!res.ok) throw new Error("failed");
      setItems((ls) => ls.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x)));
    } catch {
      setError("Mise à jour impossible.");
    }
  };

  const notify = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/properties/${id}/notify`, { method: "POST", headers });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setNotifyInfo({ id, sent: data.sent });
      setTimeout(() => setNotifyInfo(null), 6000);
    } catch {
      setError("Notification impossible.");
    }
  };

  const del = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/properties/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("failed");
      setItems((ls) => ls.filter((x) => x.id !== id));
      setConfirmId(null);
    } catch {
      setError("Suppression impossible.");
    }
  };

  const viewCounts = {
    all: items ? items.length : 0,
    public: items ? items.filter((p) => !p.off_market).length : 0,
    offmarket: items ? items.filter((p) => p.off_market).length : 0,
    drafts: items ? items.filter((p) => !p.published).length : 0,
    sold: items ? items.filter((p) => p.sold).length : 0,
  };
  const typeCounts = {
    all: items ? items.length : 0,
    sale: items ? items.filter((p) => (p.listing_type || "sale") === "sale").length : 0,
    rental: items ? items.filter((p) => p.listing_type === "rental").length : 0,
  };
  const visibleItems = (items || [])
    .filter((p) => typeFilter === "all" || (p.listing_type || "sale") === typeFilter)
    .filter((p) =>
      view === "public"
        ? !p.off_market
        : view === "offmarket"
          ? p.off_market
          : view === "drafts"
            ? !p.published
            : view === "sold"
              ? p.sold
              : true
    );
  const commercialFormArea = parseFloat(form.usable_area) || parseFloat(form.area) || 0;
  const commercialFormRate = parseFloat(form.rental_price_per_sqm_year) || 0;
  const commercialFormMode = form.commercial_pricing_mode || "per_sqm_year";
  const commercialRentalForm = form.listing_type === "rental" && form.category === "commercial";
  const commercialFixedNet = parseFloat(form.monthly_net_rent) || 0;
  const commercialFormAnnual = commercialFormMode === "per_sqm_year"
    ? commercialFormRate > 0 && commercialFormArea > 0
      ? commercialFormRate * commercialFormArea
      : parseFloat(form.annual_rent) || 0
    : commercialFixedNet * 12;
  const commercialFormNet = commercialFormMode === "per_sqm_year" ? commercialFormAnnual / 12 : commercialFixedNet;
  const commercialFormGross = commercialFormNet + parseChargesInput(form.monthly_charges).numeric;

  const toggleSold = async (p) => {
    try {
      const res = await fetch(`${API}/api/admin/properties/${p.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ ...p, sold: !p.sold }),
      });
      if (!res.ok) throw new Error("failed");
      setItems((ls) => ls.map((x) => (x.id === p.id ? { ...x, sold: !p.sold } : x)));
    } catch {
      setError("Mise à jour impossible.");
    }
  };

  return (
    <div data-testid="property-manager" className="mt-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/45">
          {items ? `${visibleItems.length} propriété${visibleItems.length > 1 ? "s" : ""}` : "…"}
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            data-testid="property-import-button"
            onClick={() => importRef.current?.click()}
            disabled={importing}
            className="flex w-full items-center justify-center gap-2 border border-ivory/25 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory/70 transition-colors duration-300 hover:border-champagne hover:text-champagne disabled:opacity-50 sm:w-auto"
          >
            {importing ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
            {importing ? "Analyse du dossier…" : "Importer un dossier PDF"}
          </button>
          <input
            ref={importRef}
            type="file"
            accept="application/pdf"
            data-testid="property-import-file-input"
            onChange={(e) => importDossier(e.target.files?.[0])}
            className="hidden"
          />
          <button
            data-testid="property-form-toggle"
            onClick={() => {
              setForm(EMPTY);
              setEditId(null);
              setFormOpen(!formOpen);
            }}
            className="flex w-full items-center justify-center gap-2 border border-bronze px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-bronze sm:w-auto"
          >
            <Plus size={13} /> Nouvelle propriété
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 sm:gap-x-8" data-testid="property-type-filter">
        {[
          { key: "all", label: "Tous types" },
          { key: "sale", label: "Vente" },
          { key: "rental", label: "Location" },
        ].map((v) => (
          <button
            key={v.key}
            data-testid={`property-type-${v.key}`}
            onClick={() => setTypeFilter(v.key)}
            className={`relative pb-3 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
              typeFilter === v.key
                ? "text-champagne after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:bg-champagne"
                : "text-ivory/40 hover:text-ivory"
            }`}
          >
            {v.label} <span className="ml-1 text-ivory/35">({typeCounts[v.key]})</span>
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 sm:gap-x-8 border-b border-ivory/10" data-testid="property-view-filter">
        {[
          { key: "all", label: "Toutes" },
          { key: "public", label: "Publiques" },
          { key: "offmarket", label: "Off-market" },
          { key: "drafts", label: "Brouillons" },
          { key: "sold", label: "Vendus" },
        ].map((v) => (
          <button
            key={v.key}
            data-testid={`property-view-${v.key}`}
            onClick={() => setView(v.key)}
            className={`relative pb-4 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
              view === v.key
                ? "text-ivory after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:bg-champagne"
                : "text-ivory/40 hover:text-ivory"
            }`}
          >
            {v.label} <span className="ml-1 text-ivory/35">({viewCounts[v.key]})</span>
          </button>
        ))}
      </div>

      {formOpen && (
        <form data-testid="property-form" onSubmit={save} className="mt-8 border border-ivory/15 p-6 md:p-8">
          {editId && (
            <div className="mb-8 flex flex-col gap-4 border border-champagne/35 bg-champagne/5 p-4 md:flex-row md:items-center md:justify-between" data-testid="property-form-dossier-export">
              <div>
                {form.ref && (
                  <p className="text-[11px] uppercase tracking-[0.2em] text-bronze" data-testid="property-ref-display">
                    Référence: {form.ref}
                  </p>
                )}
                <p className="mt-1 text-xs font-light text-ivory/55">Téléchargez le dossier PDF de la version enregistrée.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-champagne">PDF</span>
                {["fr", "de", "en"].map((lng) => (
                  <button
                    key={lng}
                    type="button"
                    data-testid={`property-form-dossier-${lng}`}
                    onClick={() => downloadDossier(form, lng)}
                    className="border border-champagne/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-ink"
                  >
                    {lng}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mb-8 flex flex-wrap gap-3" data-testid="property-listing-type-toggle">
            {[
              { key: "sale", label: "Vente" },
              { key: "rental", label: "Location" },
            ].map((type) => (
              <button
                key={type.key}
                type="button"
                data-testid={`property-type-select-${type.key}`}
                onClick={() => set("listing_type", type.key)}
                className={`border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                  form.listing_type === type.key
                    ? "border-champagne bg-champagne/10 text-champagne"
                    : "border-ivory/20 text-ivory/50 hover:border-ivory/50 hover:text-ivory"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="p-title">Titre *</label>
              <input id="p-title" data-testid="property-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="p-category">Objektart / Catégorie *</label>
              <select id="p-category" data-testid="property-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className={`${inputCls} cursor-pointer`}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value} className="bg-ink">
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {form.listing_type === "sale" ? (
              <div>
                <label className={labelCls} htmlFor="p-price">Prix d&apos;achat (CHF) *</label>
                <input id="p-price" data-testid="property-price-input" required type="number" min="0" step="1000" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
              </div>
            ) : form.category === "commercial" ? (
              <>
                <div className="md:col-span-2" data-testid="commercial-pricing-mode-toggle">
                  <span className={labelCls}>Modèle de prix commercial</span>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: "per_sqm_year", label: "CHF/m²/an" },
                      { key: "monthly_fixed", label: "Prix fixe mensuel" },
                    ].map((mode) => (
                      <button
                        key={mode.key}
                        type="button"
                        data-testid={`commercial-pricing-mode-${mode.key.replaceAll("_", "-")}`}
                        onClick={() => set("commercial_pricing_mode", mode.key)}
                        className={`border px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                          commercialFormMode === mode.key
                            ? "border-champagne bg-champagne/10 text-champagne"
                            : "border-ivory/20 text-ivory/50 hover:border-ivory/50 hover:text-ivory"
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div
                  className={`grid gap-6 sm:grid-cols-2 md:col-span-2 ${
                    commercialFormMode === "per_sqm_year" ? "xl:grid-cols-5" : "xl:grid-cols-3"
                  }`}
                  data-testid="commercial-rental-pricing-fields"
                >
                  {commercialFormMode === "per_sqm_year" ? (
                    <>
                      <div>
                        <label className={labelCls} htmlFor="p-rental-rate">Loyer (CHF/m²/an) *</label>
                        <input id="p-rental-rate" data-testid="property-rental-rate-input" required type="number" min="0" step="5" value={form.rental_price_per_sqm_year} onChange={(e) => set("rental_price_per_sqm_year", e.target.value)} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="p-annual-rent">Loyer annuel (auto)</label>
                        <input id="p-annual-rent" data-testid="property-annual-rent-input" readOnly value={commercialFormAnnual.toLocaleString("de-CH")} className={`${inputCls} cursor-not-allowed text-champagne`} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="p-commercial-net-rent">Loyer net / mois (auto)</label>
                        <input id="p-commercial-net-rent" data-testid="property-commercial-net-rent-input" readOnly value={commercialFormNet.toLocaleString("de-CH")} className={`${inputCls} cursor-not-allowed text-champagne`} />
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className={labelCls} htmlFor="p-commercial-fixed-net">Loyer net fixe (CHF/mois) *</label>
                      <input id="p-commercial-fixed-net" data-testid="property-commercial-fixed-net-input" required type="number" min="0" step="50" value={form.monthly_net_rent} onChange={(e) => set("monthly_net_rent", e.target.value)} className={inputCls} />
                    </div>
                  )}
                  <div>
                    <label className={labelCls} htmlFor="p-charges">Charges</label>
                    <input id="p-charges" data-testid="property-charges-input" type="text" value={form.monthly_charges} onChange={(e) => set("monthly_charges", e.target.value)} placeholder="150, incluses, selon consommation…" className={inputCls} />
                    <p className="mt-1 text-[10px] font-light text-ivory/35">Texte possible; un montant trouvé dans le texte est utilisé pour le calcul.</p>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="p-gross-rent">Loyer brut (auto)</label>
                    <input id="p-gross-rent" data-testid="property-gross-rent-input" readOnly value={commercialFormGross.toLocaleString("de-CH")} className={`${inputCls} cursor-not-allowed text-champagne`} />
                  </div>
                </div>
              </>
            ) : (
              <div className="grid gap-6 sm:grid-cols-3" data-testid="rental-pricing-fields">
                <div>
                  <label className={labelCls} htmlFor="p-net-rent">Loyer net (CHF/mois) *</label>
                  <input id="p-net-rent" data-testid="property-net-rent-input" required type="number" min="0" step="50" value={form.monthly_net_rent} onChange={(e) => set("monthly_net_rent", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-charges">Charges (CHF/mois)</label>
                  <input id="p-charges" data-testid="property-charges-input" type="number" min="0" step="50" value={form.monthly_charges} onChange={(e) => set("monthly_charges", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-gross-rent">Loyer brut (auto)</label>
                  <input
                    id="p-gross-rent"
                    data-testid="property-gross-rent-input"
                    readOnly
                    value={((parseFloat(form.monthly_net_rent) || 0) + (parseFloat(form.monthly_charges) || 0)).toLocaleString("de-CH")}
                    className={`${inputCls} cursor-not-allowed text-champagne`}
                  />
                </div>
              </div>
            )}
            <div className={`grid gap-6 ${commercialRentalForm ? "grid-cols-2" : "grid-cols-3"}`}>
              <div>
                <label className={labelCls} htmlFor="p-rooms">Pièces</label>
                <input id="p-rooms" data-testid="property-rooms-input" type="number" min="0" step="0.5" value={form.rooms} onChange={(e) => set("rooms", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-usable">{commercialRentalForm ? "Surface locative (m²)" : "Surface utile (m²)"}</label>
                <input id="p-usable" data-testid="property-usable-area-input" type="number" min="0" value={form.usable_area} onChange={(e) => set("usable_area", e.target.value)} className={inputCls} />
              </div>
              {!commercialRentalForm && (
                <div>
                  <label className={labelCls} htmlFor="p-area">Surface habitable nette (m²)</label>
                  <input id="p-area" data-testid="property-area-input" type="number" min="0" value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-[110px_1fr] gap-6">
              <div>
                <label className={labelCls} htmlFor="p-zip">NPA</label>
                <input id="p-zip" data-testid="property-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-city">Localité *</label>
                <input id="p-city" data-testid="property-city-input" required value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
              </div>
            </div>
            {form.listing_type === "sale" ? (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="p-floor">Étage</label>
                  <input id="p-floor" data-testid="property-floor-input" value={form.floor} onChange={(e) => set("floor", e.target.value)} placeholder="EG, 1, 2…" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-availability">Disponible dès</label>
                  <input id="p-availability" data-testid="property-availability-input" value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="Sur demande, dès maintenant…" className={inputCls} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6" data-testid="rental-availability-fields">
                <div>
                  <label className={labelCls} htmlFor="p-floor">Étage</label>
                  <input id="p-floor" data-testid="property-floor-input" value={form.floor} onChange={(e) => set("floor", e.target.value)} placeholder="EG, 1, 2…" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-deposit">Dépôt (CHF)</label>
                  <input id="p-deposit" data-testid="property-deposit-input" type="number" min="0" step="100" value={form.rental_deposit} onChange={(e) => set("rental_deposit", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-availability-status">Disponibilité</label>
                  <select id="p-availability-status" data-testid="property-availability-status-select" value={form.availability_status} onChange={(e) => set("availability_status", e.target.value)} className={`${inputCls} cursor-pointer`}>
                    <option value="available" className="bg-ink">Disponible</option>
                    <option value="by_agreement" className="bg-ink">Sur demande</option>
                    <option value="from_date" className="bg-ink">À partir du</option>
                    <option value="rented" className="bg-ink">Loué</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-availability-date">Date</label>
                  <input
                    id="p-availability-date"
                    data-testid="property-availability-date-input"
                    type="date"
                    value={form.availability_date}
                    onChange={(e) => set("availability_date", e.target.value)}
                    disabled={form.availability_status !== "from_date"}
                    className={`${inputCls} disabled:opacity-35`}
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className={labelCls} htmlFor="p-year">Année de construction</label>
                <input id="p-year" data-testid="property-year-input" value={form.year_built} onChange={(e) => set("year_built", e.target.value)} placeholder="1977" className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-renovation">Année de rénovation</label>
                <input id="p-renovation" data-testid="property-renovation-input" value={form.renovation_year} onChange={(e) => set("renovation_year", e.target.value)} placeholder="2016" className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-land">Surface du terrain (m²)</label>
                <input id="p-land" data-testid="property-land-input" type="number" min="0" value={form.land_area} onChange={(e) => set("land_area", e.target.value)} className={inputCls} />
              </div>
            </div>
            <div className="flex items-end pb-2">
              <div className="space-y-3">
                <label className="flex cursor-pointer items-center gap-3 text-sm text-ivory/70">
                  <input data-testid="property-published-checkbox" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-[#8A6A3B]" />
                  Publié (visible sur le site)
                </label>
                <label className="flex cursor-pointer items-center gap-3 text-sm text-champagne/90">
                  <input data-testid="property-offmarket-checkbox" type="checkbox" checked={form.off_market} onChange={(e) => set("off_market", e.target.checked)} className="h-4 w-4 accent-[#8A6A3B]" />
                  Off-market (espace membres uniquement)
                </label>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <label className={labelCls} htmlFor="p-description">Description</label>
            <textarea id="p-description" data-testid="property-description-input" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} resize-none`} />
          </div>
          <div className="mt-6">
            <label className={labelCls} htmlFor="p-features">Équipement (une ligne par élément)</label>
            <textarea id="p-features" data-testid="property-features-input" rows={3} value={form.features} onChange={(e) => set("features", e.target.value)} placeholder={"Balcon\nGarage\nPlace de parc"} className={`${inputCls} resize-none`} />
          </div>
          <div className="mt-6">
            <label className={labelCls} htmlFor="p-extra-facts">Informations détaillées (une ligne par info, format « Label: valeur »)</label>
            <textarea id="p-extra-facts" data-testid="property-extra-facts-input" rows={3} value={form.extra_facts} onChange={(e) => set("extra_facts", e.target.value)} placeholder={"Surface locative totale: 5 311 m²\nRendement locatif: 4.2 %"} className={`${inputCls} resize-none`} />
            <p className="mt-2 text-[10px] font-light text-ivory/35">Ces infos apparaissent dans les caractéristiques du dossier PDF et sur la page du bien.</p>
          </div>
          <div className="mt-8 border border-ivory/10 p-5" data-testid="property-translations">
            <p className="text-[10px] uppercase tracking-[0.25em] text-champagne">Traductions du dossier (optionnel)</p>
            <p className="mt-1.5 text-[10px] font-light text-ivory/35">Si remplies, ces versions sont utilisées sur le site et dans le dossier PDF selon la langue choisie. Sinon, la version française est affichée.</p>
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="p-title-de">Titre (DE)</label>
                <input id="p-title-de" data-testid="property-title-de-input" value={form.title_de} onChange={(e) => set("title_de", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-title-en">Titre (EN)</label>
                <input id="p-title-en" data-testid="property-title-en-input" value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-description-de">Description (DE)</label>
                <textarea id="p-description-de" data-testid="property-description-de-input" rows={3} value={form.description_de} onChange={(e) => set("description_de", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-description-en">Description (EN)</label>
                <textarea id="p-description-en" data-testid="property-description-en-input" rows={3} value={form.description_en} onChange={(e) => set("description_en", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-features-de">Équipement (DE, une ligne par élément)</label>
                <textarea id="p-features-de" data-testid="property-features-de-input" rows={3} value={form.features_de} onChange={(e) => set("features_de", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-features-en">Équipement (EN, une ligne par élément)</label>
                <textarea id="p-features-en" data-testid="property-features-en-input" rows={3} value={form.features_en} onChange={(e) => set("features_en", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-extra-de">Informations détaillées (DE)</label>
                <textarea id="p-extra-de" data-testid="property-extra-facts-de-input" rows={3} value={form.extra_facts_de} onChange={(e) => set("extra_facts_de", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className={labelCls} htmlFor="p-extra-en">Informations détaillées (EN)</label>
                <textarea id="p-extra-en" data-testid="property-extra-facts-en-input" rows={3} value={form.extra_facts_en} onChange={(e) => set("extra_facts_en", e.target.value)} className={`${inputCls} resize-none`} />
              </div>
            </div>
          </div>
          <div className="mt-6">
            <label className={labelCls}>Photos</label>
            <div className="mt-2 flex flex-wrap gap-3" data-testid="property-images-preview">
              {form.images.map((src, i) => (
                <div
                  key={`${src}-${i}`}
                  draggable
                  onDragStart={(e) => {
                    setDragIdx(i);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    moveImage(dragIdx, i);
                    setDragIdx(null);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`group relative h-20 w-28 cursor-grab overflow-hidden border transition-all duration-200 active:cursor-grabbing ${
                    dragIdx === i ? "scale-95 border-champagne opacity-50" : "border-ivory/15 hover:border-ivory/40"
                  }`}
                  data-testid={`property-image-${i}`}
                >
                  <img src={resolveImg(src)} alt="" draggable={false} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    data-testid={`property-image-remove-${i}`}
                    onClick={() => removeImage(i)}
                    aria-label="Supprimer"
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center bg-ink/80 text-ivory opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  >
                    <X size={11} />
                  </button>
                  {i === 0 && (
                    <span className="absolute bottom-0 left-0 bg-champagne px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-widest text-ink">
                      Couverture
                    </span>
                  )}
                </div>
              ))}
              {localPreviews.map((src, i) => (
                <div key={src} className="relative flex h-20 w-28 items-center justify-center overflow-hidden border border-champagne/40" data-testid={`property-image-uploading-${i}`}>
                  <img src={src} alt="" className="h-full w-full object-cover opacity-40" />
                  <Loader2 size={16} className="absolute animate-spin text-champagne" />
                </div>
              ))}
              <button
                type="button"
                data-testid="property-image-upload-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading > 0}
                className="flex h-20 w-28 flex-col items-center justify-center gap-1.5 border border-dashed border-ivory/25 text-ivory/45 transition-colors duration-300 hover:border-champagne hover:text-champagne disabled:opacity-50"
              >
                <ImagePlus size={16} />
                <span className="px-1 text-center text-[8px] uppercase tracking-widest">
                  {uploading > 0 ? `Upload… (${uploading})` : "Ajouter"}
                </span>
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              data-testid="property-image-file-input"
              onChange={(e) => uploadImages(e.target.files)}
              className="hidden"
            />
            <p className="mt-2 text-[10px] font-light text-ivory/35">JPG, PNG ou WebP · max 10 MB par image · glisser-déposer pour réordonner · la première image sert de couverture</p>
          </div>
          <div className="mt-8 flex gap-3">
            <button
              type="submit"
              data-testid="property-save-button"
              disabled={saving}
              className="border border-bronze px-8 py-3.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-bronze disabled:opacity-50"
            >
              {saving ? "Enregistrement…" : editId ? "Mettre à jour" : "Enregistrer"}
            </button>
            <button
              type="button"
              data-testid="property-cancel-button"
              onClick={() => {
                setFormOpen(false);
                setEditId(null);
                setForm(EMPTY);
              }}
              className="px-5 py-3.5 text-[10px] uppercase tracking-[0.2em] text-ivory/45 transition-colors duration-300 hover:text-ivory"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {!items ? (
        <p className="mt-10 text-ivory/50">Chargement…</p>
      ) : visibleItems.length === 0 ? (
        <div className="mt-10 border border-ivory/10 px-12 py-24 text-center" data-testid="property-list-empty">
          <p className="font-serif text-xl text-ivory/60">Aucune propriété</p>
          <p className="mt-2 text-xs font-light text-ivory/35">
            {view === "public"
              ? "Aucune propriété publique. Créez-en une ci-dessus."
              : view === "offmarket"
                ? "Aucune propriété off-market. Cochez « Off-market » dans le formulaire."
                : view === "drafts"
                  ? "Aucun brouillon. Toutes vos propriétés sont publiées."
                  : view === "sold"
                    ? "Aucune propriété vendue pour le moment."
                    : "Aucune propriété. Créez la première ci-dessus."}
          </p>
        </div>
      ) : (
        <div className="mt-2 divide-y divide-ivory/10 border-b border-ivory/10" data-testid="property-list">
          {visibleItems.map((p) => (
            <div key={p.id} data-testid={`admin-property-${p.id}`} className="group flex flex-col gap-5 px-2 py-7 transition-colors duration-300 hover:bg-ivory/[0.02] md:flex-row md:items-center md:gap-8">
              {p.images && p.images[0] ? (
                <img src={resolveImg(p.images[0])} alt="" className="h-24 w-full border border-ivory/10 object-cover grayscale transition-all duration-700 group-hover:grayscale-0 md:w-40" />
              ) : (
                <div className="flex h-24 w-full items-center justify-center border border-ivory/10 bg-ivory/5 text-[9px] uppercase tracking-widest text-ivory/30 md:w-40">
                  —
                </div>
              )}
              <div className="min-w-[200px] flex-1">
                <p className="font-serif text-xl text-ivory">
                  {p.title}
                </p>
                <p className="mt-1.5 text-xs text-ivory/45">
                  {CAT_LABELS[p.category]} · {p.zip_code} {p.city}
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {p.ref && <span className="text-[10px] uppercase tracking-[0.2em] text-champagne">{p.ref}</span>}
                  <span
                    data-testid={`property-type-badge-${p.id}`}
                    className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                      p.listing_type === "rental" ? "border-champagne/60 text-champagne" : "border-ivory/20 text-ivory/45"
                    }`}
                  >
                    {p.listing_type === "rental" ? "Location" : "Vente"}
                  </span>
                  {p.listing_type === "rental" && p.availability_status === "rented" && (
                    <span data-testid={`property-rented-badge-${p.id}`} className="bg-champagne px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
                      Loué
                    </span>
                  )}
                  {p.sold && (
                    <span data-testid={`property-sold-badge-${p.id}`} className="bg-champagne px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink">
                      Vendu{p.sold_at ? ` · ${new Date(p.sold_at).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                    </span>
                  )}
                  <span
                    className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${
                      p.published ? "border-champagne/50 text-champagne" : "border-ivory/20 text-ivory/40"
                    }`}
                  >
                    {p.published ? "Publié" : "Brouillon"}
                  </span>
                  {p.off_market && (
                    <span className="border border-bronze/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-bronze">
                      Off-market
                    </span>
                  )}
                </div>
              </div>
              <p className="font-serif text-lg text-ivory/80 md:text-right" data-testid={`property-admin-price-${p.id}`}>
                {adminPropertyPrice(p)}
              </p>
              <div className="flex flex-wrap items-center gap-2 md:justify-end">
                {dossierFor === p.id ? (
                  <span className="flex items-center gap-1 border border-champagne/50 px-2 py-1.5" data-testid={`property-dossier-langs-${p.id}`}>
                    <FileText size={12} className="text-champagne" />
                    {["fr", "de", "en"].map((lng) => (
                      <button
                        key={lng}
                        data-testid={`property-dossier-${lng}-${p.id}`}
                        onClick={() => {
                          downloadDossier(p, lng);
                          setDossierFor(null);
                        }}
                        className="px-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-champagne transition-colors duration-200 hover:text-ivory"
                      >
                        {lng}
                      </button>
                    ))}
                    <button
                      data-testid={`property-dossier-close-${p.id}`}
                      onClick={() => setDossierFor(null)}
                      aria-label="Fermer"
                      className="px-1 text-ivory/40 transition-colors duration-200 hover:text-ivory"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ) : (
                  <button
                    data-testid={`property-dossier-${p.id}`}
                    onClick={() => setDossierFor(p.id)}
                    className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-champagne hover:text-champagne"
                  >
                    <FileText size={12} /> Dossier
                  </button>
                )}
                {p.listing_type !== "rental" && (
                  <button
                    data-testid={`property-sold-${p.id}`}
                    onClick={() => toggleSold(p)}
                    className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                      p.sold
                        ? "border-champagne bg-champagne/10 text-champagne hover:bg-champagne hover:text-ink"
                        : "border-ivory/20 text-ivory/60 hover:border-champagne hover:text-champagne"
                    }`}
                  >
                    {p.sold ? "Remettre en vente" : "Vendu"}
                  </button>
                )}
                {p.off_market && p.published && !p.sold && p.availability_status !== "rented" && (
                  <button
                    data-testid={`property-notify-${p.id}`}
                    onClick={() => notify(p.id)}
                    className="border border-champagne/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-ink"
                  >
                    {notifyInfo && notifyInfo.id === p.id ? `✓ Envoyé (${notifyInfo.sent})` : "Notifier les membres"}
                  </button>
                )}
                <button
                  data-testid={`property-edit-${p.id}`}
                  onClick={() => startEdit(p)}
                  className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory"
                >
                  <Pencil size={12} /> Modifier
                </button>
                <button
                  data-testid={`property-publish-${p.id}`}
                  onClick={() => togglePublish(p)}
                  className="border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-bronze hover:text-ivory"
                >
                  {p.published ? "Dépublier" : "Publier"}
                </button>
                {confirmId === p.id ? (
                  <span className="flex items-center gap-2">
                    <button
                      data-testid={`property-delete-yes-${p.id}`}
                      onClick={() => del(p.id)}
                      className="border border-red-400/60 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-red-300 transition-colors duration-300 hover:bg-red-400/10"
                    >
                      Confirmer
                    </button>
                    <button
                      data-testid={`property-delete-no-${p.id}`}
                      onClick={() => setConfirmId(null)}
                      className="px-2 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/40 transition-colors duration-300 hover:text-ivory"
                    >
                      Annuler
                    </button>
                  </span>
                ) : (
                  <button
                    data-testid={`property-delete-${p.id}`}
                    onClick={() => setConfirmId(p.id)}
                    className="flex items-center gap-1.5 border border-ivory/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-ivory/60 transition-colors duration-300 hover:border-red-400/60 hover:text-red-300"
                  >
                    <Trash2 size={12} /> Supprimer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
